/**
 * Cinematic Fit — Sekcija 2: Raspad kompleta (le-fit.mp4 scrub).
 * Unified mode: sluša `le-cine-fit` (active, progress) — pokreće hero.js.
 * Standalone mode: sopstveni ScrollTrigger pin.
 */
(function () {
  const SCRUB = 0.4;
  const HEADING_CENTERS = [0.16, 0.5, 0.84];

  function parseConfig(section) {
    const el = section.querySelector("[data-fit-config]");
    if (!el) return { scrollHeightVh: 260, videoEnd: 1.0 };
    try {
      return JSON.parse(el.textContent);
    } catch (e) {
      return { scrollHeightVh: 260, videoEnd: 1.0 };
    }
  }

  function initFit(section) {
    if (section.dataset.fitInit === "1") return;
    section.dataset.fitInit = "1";

    const config = parseConfig(section);
    const track = section.querySelector("[data-track]");
    const pin = section.querySelector("[data-pin]");
    const scene = section.querySelector("[data-scene]");
    const video = section.querySelector("[data-fit-video]");
    const copy = section.querySelector("[data-copy]");
    const scrollPip = section.querySelector("[data-scroll-pip]");
    const headingStages = section.querySelectorAll(
      "[data-heading-stages] .cf__heading-stage"
    );

    if (!video) return;

    const unified = !!document.querySelector("[data-cine-unified]");

    if (unified) {
      section.classList.add("cf--unified");
      if (track) {
        track.style.setProperty("--scroll-vh", "0");
        track.style.height = "0";
      }
    } else if (track) {
      track.style.setProperty("--scroll-vh", String(config.scrollHeightVh));
    }

    let duration = 0;
    let targetTime = 0;
    let rafId = null;
    let primed = false;

    const tickSeek = () => {
      rafId = null;
      if (!duration) return;
      try {
        if (Math.abs(video.currentTime - targetTime) > 0.008) {
          video.currentTime = targetTime;
        }
      } catch (e) {
        /* ignore */
      }
    };

    const scheduleSeek = () => {
      if (rafId !== null) return;
      rafId = requestAnimationFrame(tickSeek);
    };

    const prime = () => {
      if (primed) return;
      primed = true;
      try {
        video.removeAttribute("poster");
      } catch (e) {}
      const run = () => {
        try {
          const playP = video.play();
          if (playP && typeof playP.then === "function") {
            playP.then(() => video.pause()).catch(() => {});
          }
        } catch (e) {}
      };
      if (video.readyState >= 2) run();
      else video.addEventListener("loadeddata", run, { once: true });
    };

    const onMeta = () => {
      duration = video.duration || 0;
    };
    if (video.readyState >= 1 && video.duration) onMeta();
    else video.addEventListener("loadedmetadata", onMeta, { once: true });
    try { video.load(); } catch (e) {}

    const seek = (ratio) => {
      if (!duration) duration = video.duration || 0;
      if (!duration) return;
      const next =
        Math.max(0, Math.min(1, ratio)) * duration * (config.videoEnd || 1);
      if (Math.abs(next - targetTime) < 0.002) return;
      targetTime = next;
      scheduleSeek();
    };

    /* Anchor positions for the copy block at each heading stage (desktop).
       Recomputed on resize via cached + invalidate pattern. Stage 0 is
       center-left, stage 1 center-right, stage 2 bottom-left.
       Position is then interpolated continuously between adjacent anchors
       in updateCopyPosition() — perfectly synced with heading crossfade. */
    let cachedAnchors = null;
    let cachedW = 0;
    let cachedH = 0;
    const computeAnchors = () => {
      const W = window.innerWidth;
      const H = window.innerHeight;
      if (cachedAnchors && cachedW === W && cachedH === H) return cachedAnchors;
      cachedW = W;
      cachedH = H;
      /* Same math as the CSS clamp() expressions so anchor positions
         match the visual margins exactly:
         marginX = clamp(3.5rem=56px, 9vw, 6.5rem=104px)
         marginBottom = clamp(3rem=48px, 8vh, 5rem=80px)
         blockW = min(440px, 38vw) */
      const marginX = Math.max(56, Math.min(104, W * 0.09));
      const marginBottom = Math.max(48, Math.min(80, H * 0.08));
      const blockW = Math.min(440, W * 0.38);
      cachedAnchors = [
        { x: marginX,              y: H * 0.5,           yPct: -50 },
        { x: W - blockW - marginX, y: H * 0.5,           yPct: -50 },
        { x: marginX,              y: H - marginBottom,  yPct: -100 },
      ];
      return cachedAnchors;
    };

    const smoothstep = (t) => t * t * (3 - 2 * t);

    let lastProgress = 0;
    const updateCopyPosition = (progress) => {
      if (!copy) return;
      /* Standalone mode: keep the original CSS-driven position
         (bottom-left, gsap entrance animation). The 3-anchor cycle is a
         unified-mode design — it only makes sense alongside the rest of
         the cinematic scroll. */
      if (!unified) return;
      lastProgress = progress;
      /* Mobile: base CSS handles position (bottom: 16vh + left clamp). Clear
         any leftover inline transform from a prior desktop layout (e.g. user
         resized window across the 721px threshold). */
      if (window.innerWidth < 721) {
        if (copy.style.transform) copy.style.transform = "";
        return;
      }

      const anchors = computeAnchors();
      const cs = HEADING_CENTERS;
      let pos;
      if (progress <= cs[0]) {
        pos = anchors[0];
      } else if (progress >= cs[cs.length - 1]) {
        pos = anchors[cs.length - 1];
      } else {
        let i = 0;
        while (i < cs.length - 1 && progress > cs[i + 1]) i++;
        const t = (progress - cs[i]) / (cs[i + 1] - cs[i]);
        const e = smoothstep(t);
        pos = {
          x: anchors[i].x + (anchors[i + 1].x - anchors[i].x) * e,
          y: anchors[i].y + (anchors[i + 1].y - anchors[i].y) * e,
          yPct: anchors[i].yPct + (anchors[i + 1].yPct - anchors[i].yPct) * e,
        };
      }
      copy.style.transform =
        "translate(" + pos.x.toFixed(1) + "px, " + pos.y.toFixed(1) + "px) " +
        "translateY(" + pos.yPct.toFixed(1) + "%)";

      /* Dominant stage class drives text-align + eyebrow flex alignment
         (those are discrete properties — they can't smoothly interpolate
         anyway, so a class swap at the heading-crossfade midpoint is the
         least visible moment to flip them). */
      let dominant = 0;
      let minDist = Infinity;
      for (let i = 0; i < HEADING_CENTERS.length; i++) {
        const d = Math.abs(progress - HEADING_CENTERS[i]);
        if (d < minDist) {
          minDist = d;
          dominant = i;
        }
      }
      if (copy.dataset.atStage !== String(dominant)) {
        copy.dataset.atStage = String(dominant);
        copy.classList.remove(
          "cf__copy--at-0",
          "cf__copy--at-1",
          "cf__copy--at-2"
        );
        copy.classList.add("cf__copy--at-" + dominant);
      }
    };

    if (unified) {
      window.addEventListener(
        "resize",
        () => {
          cachedAnchors = null;
          updateCopyPosition(lastProgress);
        },
        { passive: true }
      );
    }

    const updateHeadingMorph = (progress) => {
      if (!headingStages || !headingStages.length) return;
      const stages = Array.from(headingStages);
      const fadeIn = 0.08;
      const widths = 0.22;
      stages.forEach((el, i) => {
        const center = HEADING_CENTERS[i] !== undefined ? HEADING_CENTERS[i] : 0.5;
        const d = Math.abs(progress - center);
        let op;
        if (d <= widths - fadeIn) op = 1;
        else if (d >= widths) op = 0;
        else op = 1 - (d - (widths - fadeIn)) / fadeIn;
        const y = (progress - center) * 56;
        el.style.opacity = op.toFixed(3);
        el.style.transform = `translateY(${y.toFixed(1)}px)`;
      });

      updateCopyPosition(progress);
    };

    const applyProgress = (progress) => {
      const p = Math.max(0, Math.min(1, progress));
      if (scrollPip) scrollPip.style.setProperty("--scroll-progress", p * 100 + "%");
      if (p > 0.001) prime();
      seek(p);
      updateHeadingMorph(p);
    };

    if (unified) {
      window.addEventListener("le-cine-fit", (e) => {
        const { active, progress } = e.detail || {};
        if (active) {
          section.classList.add("cf--live", "cf--raspad-active");
          section.classList.remove("cf--past");
          applyProgress(typeof progress === "number" ? progress : 0);
        } else {
          section.classList.remove("cf--live", "cf--raspad-active");
          section.querySelectorAll(".cf__chrome").forEach((el) => {
            el.style.opacity = "";
          });
        }
      });
      return;
    }

    /* Standalone mode */
    const initScroll = () => {
      if (typeof gsap === "undefined" || typeof ScrollTrigger === "undefined") {
        window.addEventListener("load", initScroll, { once: true });
        return;
      }
      gsap.registerPlugin(ScrollTrigger);
      ScrollTrigger.create({
        trigger: track,
        start: "top top",
        end: "bottom bottom",
        scrub: SCRUB,
        pin: pin,
        pinSpacing: true,
        anticipatePin: 1,
        invalidateOnRefresh: true,
        onEnter: () => section.classList.add("cf--live", "cf--raspad-active"),
        onEnterBack: () => section.classList.add("cf--live", "cf--raspad-active"),
        onLeaveBack: () => section.classList.remove("cf--live", "cf--raspad-active"),
        onUpdate: (self) => applyProgress(self.progress),
      });
      if (video) {
        gsap.fromTo(
          video,
          { scale: 0.96 },
          {
            scale: 1.04,
            ease: "none",
            scrollTrigger: { trigger: track, start: "top top", end: "bottom bottom", scrub: SCRUB },
          }
        );
      }
      if (copy) {
        gsap.fromTo(copy, { opacity: 0, y: 24 }, { opacity: 1, y: 0, duration: 0.9, ease: "power3.out", delay: 0.2 });
      }
    };
    initScroll();
  }

  const boot = () => {
    document.querySelectorAll("[data-cinematic-fit]").forEach(initFit);
  };

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", boot);
  } else {
    boot();
  }
  document.addEventListener("shopify:section:load", boot);
})();
